import { useState, useEffect } from 'react';
import telemetryService, {
  type EngagementAnalytics,
  type ReengagementTarget,
  type InactiveUser,
  type NeverUsedUser
} from '../../../services/telemetryService';
import { MODULES } from '../../../telemetry/constants/modules';

interface TelemetryAnalyticsProps {
  onTooltipShow?: (title: string, content: string) => void;
}

export default function TelemetryAnalytics({ onTooltipShow }: TelemetryAnalyticsProps) {
  // Navigation tabs
  const [activeSubTab, setActiveSubTab] = useState<'engagement' | 'inactivity' | 'optimization'>('engagement');

  // API states
  const [engagement, setEngagement] = useState<EngagementAnalytics[]>([]);
  const [reengagementTargets, setReengagementTargets] = useState<ReengagementTarget[]>([]);
  const [optimizationFlags, setOptimizationFlags] = useState<any[]>([]);

  // Filtering states for inactive / never used users
  const [selectedModule, setSelectedModule] = useState<string>('SEO Manager');
  const [inactiveDays, setInactiveDays] = useState<number>(14);
  const [inactiveUsers, setInactiveUsers] = useState<InactiveUser[]>([]);
  const [neverUsedUsers, setNeverUsedUsers] = useState<NeverUsedUser[]>([]);
  const [loadingInactive, setLoadingInactive] = useState<boolean>(false);

  // Search filter
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Main UI States
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const modulesList = Object.values(MODULES);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch top-level summaries parallelly
      const [engRes, reengageRes, optRes] = await Promise.all([
        telemetryService.getEngagement(),
        telemetryService.getReengagementTargets(inactiveDays),
        telemetryService.getOptimizationFlags(7)
      ]);

      setEngagement(engRes);
      setReengagementTargets(reengageRes);
      setOptimizationFlags(optRes);

    } catch (err) {
      console.error('Failed to load telemetry analytics data:', err);
      setError(err instanceof Error ? err.message : 'Failed to retrieve telemetry data from the server.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchModuleSpecificUsers = async () => {
    try {
      setLoadingInactive(true);
      const [inactiveRes, neverRes] = await Promise.all([
        telemetryService.getInactiveUsers(selectedModule, inactiveDays),
        telemetryService.getNeverUsedUsers(selectedModule)
      ]);
      setInactiveUsers(inactiveRes);
      setNeverUsedUsers(neverRes);
    } catch (err) {
      console.error('Failed to load module inactive users:', err);
    } finally {
      setLoadingInactive(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [inactiveDays]);

  useEffect(() => {
    fetchModuleSpecificUsers();
  }, [selectedModule, inactiveDays]);

  // Aggregate sums
  const totalInteractions = engagement.reduce((acc, curr) => acc + (curr.aggregate_interactions || 0), 0);
  const totalMinutes = engagement.reduce((acc, curr) => acc + (curr.aggregate_minutes_logged || 0), 0);
  const totalActiveUsers = engagement.reduce((acc, curr) => acc + (curr.unique_active_users || 0), 0);

  if (isLoading) {
    return (
      <div className="py-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-teal-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium text-sm">Aggregating telemetry diagnostics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 max-w-lg mx-auto text-center">
        <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-rose-100">
          <i className="ri-error-warning-line text-rose-500 text-3xl"></i>
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-2">Telemetry Pipeline Offline</h3>
        <p className="text-slate-500 text-sm mb-6">{error}</p>
        <button
          onClick={fetchData}
          className="px-5 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl text-sm font-semibold transition-all shadow-sm"
        >
          <i className="ri-refresh-line mr-2"></i> Reconnect Diagnostics
        </button>
      </div>
    );
  }

  // Filter inactive/re-engagement users
  const filteredReengage = reengagementTargets.filter(
    u => u.username.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Title & Top Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">System Telemetry & Activity</h2>
          <p className="text-slate-500 text-sm">Real-time user engagement summaries, system diagnostic events, and optimization targets.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={inactiveDays}
            onChange={(e) => setInactiveDays(Number(e.target.value))}
            className="text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-600 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value={7}>7 Days Window</option>
            <option value={14}>14 Days Window</option>
            <option value={30}>30 Days Window</option>
          </select>
          <button
            onClick={fetchData}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold transition-colors"
          >
            <i className="ri-refresh-line"></i> Refresh
          </button>
        </div>
      </div>

      {/* Global Performance Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* interactions card */}
        <div className="bg-gradient-to-br from-indigo-50 to-blue-50/50 border border-indigo-100/50 rounded-2xl p-6 shadow-sm relative overflow-hidden group">
          <div className="absolute right-[-10px] bottom-[-10px] text-[80px] text-indigo-200/20 font-bold select-none group-hover:scale-105 transition-transform duration-300">
            <i className="ri-cursor-line"></i>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-indigo-500 uppercase tracking-wider">Aggregate Clicks / Actions</span>
            <div className="w-8 h-8 bg-indigo-500/10 rounded-lg flex items-center justify-center">
              <i className="ri-cursor-fill text-indigo-600"></i>
            </div>
          </div>
          <h3 className="text-3xl font-extrabold text-slate-900 mb-1">{totalInteractions.toLocaleString()}</h3>
          <p className="text-slate-500 text-xs font-medium">Total clicks and actions executed in application modules</p>
        </div>

        {/* users card */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50/50 border border-emerald-100/50 rounded-2xl p-6 shadow-sm relative overflow-hidden group">
          <div className="absolute right-[-10px] bottom-[-10px] text-[80px] text-emerald-200/20 font-bold select-none group-hover:scale-105 transition-transform duration-300">
            <i className="ri-user-line"></i>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-emerald-500 uppercase tracking-wider">Active Modules Users</span>
            <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <i className="ri-team-fill text-emerald-600"></i>
            </div>
          </div>
          <h3 className="text-3xl font-extrabold text-slate-900 mb-1">{totalActiveUsers.toLocaleString()}</h3>
          <p className="text-slate-500 text-xs font-medium">Unique active user traces captured across product layers</p>
        </div>

        {/* time spent card */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50/50 border border-purple-100/50 rounded-2xl p-6 shadow-sm relative overflow-hidden group">
          <div className="absolute right-[-10px] bottom-[-10px] text-[80px] text-purple-200/20 font-bold select-none group-hover:scale-105 transition-transform duration-300">
            <i className="ri-time-line"></i>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-purple-500 uppercase tracking-wider">Minutes Logged</span>
            <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <i className="ri-time-fill text-purple-600"></i>
            </div>
          </div>
          <h3 className="text-3xl font-extrabold text-slate-900 mb-1">{totalMinutes.toLocaleString()}</h3>
          <p className="text-slate-500 text-xs font-medium">Total active session duration tracked via heartbeat telemetry</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-100 gap-6">
        <button
          onClick={() => setActiveSubTab('engagement')}
          className={`pb-3 text-sm font-bold border-b-2 transition-all flex items-center gap-1.5 ${
            activeSubTab === 'engagement' ? 'border-teal-500 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <i className="ri-bar-chart-2-line"></i> Module Engagement
        </button>
        <button
          onClick={() => setActiveSubTab('inactivity')}
          className={`pb-3 text-sm font-bold border-b-2 transition-all flex items-center gap-1.5 ${
            activeSubTab === 'inactivity' ? 'border-teal-500 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <i className="ri-user-unfollow-line"></i> Inactivity & Re-engagement
        </button>
        <button
          onClick={() => setActiveSubTab('optimization')}
          className={`pb-3 text-sm font-bold border-b-2 transition-all flex items-center gap-1.5 ${
            activeSubTab === 'optimization' ? 'border-teal-500 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <i className="ri-sparkling-line"></i> Optimization Flags
          {optimizationFlags.length > 0 && (
            <span className="px-1.5 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full">{optimizationFlags.length}</span>
          )}
        </button>
      </div>

      {/* Tab Contents */}
      {activeSubTab === 'engagement' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {engagement.length === 0 ? (
              <div className="col-span-full bg-slate-50 border border-slate-200 rounded-2xl p-10 text-center text-slate-400">
                <i className="ri-inbox-line text-4xl mb-2 block"></i>
                No module engagement telemetry recorded yet.
              </div>
            ) : (
              engagement.map((item, idx) => {
                // Determine premium accents dynamically
                const colors = [
                  { border: 'hover:border-blue-400', accent: 'bg-blue-500/10 text-blue-600', fill: 'bg-blue-600' },
                  { border: 'hover:border-emerald-400', accent: 'bg-emerald-500/10 text-emerald-600', fill: 'bg-emerald-600' },
                  { border: 'hover:border-purple-400', accent: 'bg-purple-500/10 text-purple-600', fill: 'bg-purple-600' },
                  { border: 'hover:border-amber-400', accent: 'bg-amber-500/10 text-amber-600', fill: 'bg-amber-600' },
                  { border: 'hover:border-pink-400', accent: 'bg-pink-500/10 text-pink-600', fill: 'bg-pink-600' },
                ];
                const activeColor = colors[idx % colors.length];

                return (
                  <div
                    key={item.module_name}
                    className={`bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all duration-300 ${activeColor.border}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-bold text-slate-800 text-[16px] leading-tight">{item.module_name}</h4>
                      <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full uppercase tracking-wider ${activeColor.accent}`}>
                        Active Insights
                      </span>
                    </div>

                    <div className="space-y-4">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-400 font-semibold">Total Click Events</span>
                        <span className="font-bold text-slate-700">{item.aggregate_interactions.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-400 font-semibold">Minutes Active</span>
                        <span className="font-bold text-slate-700">{Math.round(item.aggregate_minutes_logged).toLocaleString()} min</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-400 font-semibold">Unique Active Traces</span>
                        <span className="font-bold text-slate-700">{item.unique_active_users.toLocaleString()}</span>
                      </div>

                      {/* Visual Health Gauge */}
                      <div className="pt-2">
                        <div className="flex justify-between items-center mb-1 text-[10px] font-bold text-slate-400">
                          <span>RELATIVE USAGE LOAD</span>
                          <span>{Math.min(100, Math.round((item.aggregate_interactions / (totalInteractions || 1)) * 100))}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${activeColor.fill} rounded-full`}
                            style={{ width: `${Math.min(100, Math.round((item.aggregate_interactions / (totalInteractions || 1)) * 100))}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'inactivity' && (
        <div className="space-y-6">
          {/* Sub Filters */}
          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1">
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1">Target Module Diagnostic</label>
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  className="text-sm bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {modulesList.map(mod => <option key={mod} value={mod}>{mod}</option>)}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1">Lookup Search</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Filter by name/email"
                  className="text-sm bg-white border border-slate-200 rounded-xl px-4 py-2 text-slate-700 font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Double Column for inactive list & never used */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left side: Module-specific Inactive Users */}
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-50">
                <div>
                  <h4 className="font-bold text-slate-800 text-[16px] flex items-center gap-1.5">
                    <i className="ri-user-shared-line text-blue-500"></i> Inactive on {selectedModule}
                  </h4>
                  <p className="text-[11px] text-slate-400">Users inactive for more than {inactiveDays} days in this module.</p>
                </div>
                <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-bold rounded-full">
                  {inactiveUsers.length} Targets
                </span>
              </div>

              {loadingInactive ? (
                <div className="py-10 text-center text-slate-400 text-sm">
                  <div className="w-6 h-6 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-2"></div>
                  Querying inactivity tables...
                </div>
              ) : inactiveUsers.length === 0 ? (
                <div className="py-10 text-center text-slate-400 text-xs">
                  <i className="ri-shield-user-line text-3xl mb-2 text-slate-200 block"></i>
                  No inactive users found. Everyone is active!
                </div>
              ) : (
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                  {inactiveUsers
                    .filter(u => u.username.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-xl hover:bg-slate-100/50 transition-colors">
                        <div>
                          <p className="font-semibold text-slate-800 text-xs">{item.username}</p>
                          <p className="text-[10px] text-slate-500">{item.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] text-slate-400">Last visit</p>
                          <p className="text-xs font-bold text-slate-600">{new Date(item.last_visited_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Right side: Never used this module */}
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-50">
                <div>
                  <h4 className="font-bold text-slate-800 text-[16px] flex items-center gap-1.5">
                    <i className="ri-user-star-line text-emerald-500"></i> Never Used {selectedModule}
                  </h4>
                  <p className="text-[11px] text-slate-400">Onboarded client users who haven't launched this feature.</p>
                </div>
                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs font-bold rounded-full">
                  {neverUsedUsers.length} Users
                </span>
              </div>

              {loadingInactive ? (
                <div className="py-10 text-center text-slate-400 text-sm">
                  <div className="w-6 h-6 border-2 border-slate-200 border-t-emerald-500 rounded-full animate-spin mx-auto mb-2"></div>
                  Resolving brand histories...
                </div>
              ) : neverUsedUsers.length === 0 ? (
                <div className="py-10 text-center text-slate-400 text-xs">
                  <i className="ri-award-line text-3xl mb-2 text-slate-200 block"></i>
                  Amazing! 100% of onboarded users have tried this module.
                </div>
              ) : (
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                  {neverUsedUsers
                    .filter(u => u.username.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div>
                          <p className="font-semibold text-slate-800 text-xs">{item.username}</p>
                          <p className="text-[10px] text-slate-500">{item.email}</p>
                        </div>
                        <span className="px-2 py-1 bg-amber-50 text-amber-600 rounded-lg text-[9px] font-bold uppercase tracking-wider">
                          Zero Traces
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Re-engagement Targets Full Table */}
          <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h4 className="font-bold text-slate-800 text-[16px] flex items-center gap-1.5">
                  <i className="ri-mail-send-line text-purple-500"></i> Global Re-engagement Pipeline
                </h4>
                <p className="text-[11px] text-slate-400">High-priority contact opportunities for brand managers based on inactivity duration.</p>
              </div>
              <span className="px-2.5 py-1 bg-purple-50 text-purple-600 text-xs font-bold rounded-full">
                {filteredReengage.length} Targets Found
              </span>
            </div>

            {filteredReengage.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                <i className="ri-inbox-archive-line text-4xl mb-2 block"></i>
                <p className="text-sm font-medium">No re-engagement targets matched the parameters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      <th className="py-3 px-4">Client User / Username</th>
                      <th className="py-3 px-4">Email</th>
                      <th className="py-3 px-4">Module Hub</th>
                      <th className="py-3 px-4">Last Event Activity</th>
                      <th className="py-3 px-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReengage.map((target, idx) => (
                      <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors text-xs text-slate-700">
                        <td className="py-3.5 px-4 font-bold text-slate-800">{target.username}</td>
                        <td className="py-3.5 px-4 text-slate-500">{target.email}</td>
                        <td className="py-3.5 px-4">
                          <span className="px-2 py-0.5 bg-slate-100 rounded-full font-semibold text-slate-600">
                            {target.module_name}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 font-semibold text-slate-600">{new Date(target.last_visited_at).toLocaleDateString()}</td>
                        <td className="py-3.5 px-4 text-right">
                          <a
                            href={`mailto:${target.email}?subject=Boost Your Brand Engagement with Marketing4Sight&body=Hello ${target.username}, we noticed you haven't used our ${target.module_name} lately!`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-[10px] font-bold uppercase transition-colors"
                          >
                            <i className="ri-mail-line"></i> Engage
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'optimization' && (
        <div className="space-y-6">
          <div className="bg-amber-50/50 border border-amber-100 rounded-2xl p-5 flex items-start gap-3">
            <i className="ri-information-line text-amber-500 text-xl flex-shrink-0 mt-0.5"></i>
            <div>
              <h5 className="font-bold text-amber-800 text-sm">About System Optimization Flags</h5>
              <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                Flags are generated dynamically by analyzing user inactivity patterns across high-impact platform features.
                When a user remains inactive beyond predefined thresholds, the system flags it as an opportunity for proactive product engagement.
              </p>
            </div>
          </div>

          {optimizationFlags.length === 0 ? (
            <div className="bg-white border border-slate-100 rounded-2xl p-16 text-center text-slate-400 shadow-sm">
              <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-slate-300 text-3xl"></i>
              </div>
              <h4 className="text-slate-800 font-bold mb-1">All Systems Optimal</h4>
              <p className="text-xs">No active telemetry optimization flags identified for the selected timeframe.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {optimizationFlags.map((flag, idx) => {
                // Ensure severities map nicely
                const severity = flag.severity || 'medium';
                const severityColors = {
                  high: 'bg-red-50 text-red-600 border-red-100',
                  medium: 'bg-amber-50 text-amber-600 border-amber-100',
                  low: 'bg-blue-50 text-blue-600 border-blue-100',
                };
                const color = severityColors[severity as keyof typeof severityColors] || severityColors.medium;

                return (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow"
                  >
                    <div>
                      <div className="flex justify-between items-start gap-4 mb-3">
                        <span className={`px-2 py-0.5 border text-[9px] font-bold rounded-full uppercase tracking-wider ${color}`}>
                          {severity} Priority
                        </span>
                        <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">{flag.module_name || 'System'}</span>
                      </div>
                      <h4 className="font-extrabold text-slate-800 text-[15px] mb-2">{flag.flag_name || 'Inactivity Flag'}</h4>
                      <p className="text-slate-500 text-xs leading-relaxed mb-4">{flag.description || 'Actionable intelligence pending diagnostics.'}</p>
                    </div>

                    <div className="border-t border-slate-50 pt-4 mt-2">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-400 font-semibold">Suggested Remediation:</span>
                        <span className="font-bold text-teal-600">{flag.suggested_action || 'Client Outreach'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
